import { Alert, Box } from '@mantine/core';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { useAllCustomers } from '@/hooks/useAllCustomers';
import { MinimalAppSkeleton } from '@/components/ui/app-skeleton';
import CustomerSelectionHeader from '@/components/customers/CustomerSelectionHeader';
import CustomerSelectionGrid from '@/components/customers/CustomerSelectionGrid';
import CustomerErrorState from '@/components/customers/CustomerErrorState';
import NoCustomersState from '@/components/customers/NoCustomersState';
import { createScopedLogger } from '@/utils/debug';
import { returnTo } from './returnTo';
import { SessionUnreachable } from './SessionUnreachable';

export const CustomerRedirect = () => {
  const {
    isAuthenticated,
    isInitialized,
    isLoading,
    isUnreachable,
    user,
    activeCustomer,
    selectCustomer,
    logout
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const [showSelectionUI, setShowSelectionUI] = useState(false);
  const [customerSelectError, setCustomerSelectError] = useState<string | null>(null);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [loadingCustomerId, setLoadingCustomerId] = useState<string | null>(null);
  const autoSelectionAttemptedRef = useRef(false);

  // Get the intended destination from location state or default to dashboard
  const from = location.state?.from || '/dashboard';

  // ``?force=1`` (set by the topbar "Switch Customer" link) opts out
  // of the auto-pick logic so an operator who already has an active
  // customer can still land on the selection grid to switch. Without
  // this, hitting ``/select-customer`` from anywhere just bounces
  // straight back to the current dashboard.
  const forcePicker = new URLSearchParams(location.search).get('force') === '1';

  // This is the explicit customer-picker surface, so it is the right place
  // to fetch the full list. Only fetch when we're actually going to show
  // the picker — i.e. the user forced it, or they're authenticated without
  // an active customer. When an active customer is already set (and not
  // forcing), we just bounce to ``from`` and never touch the list. The auth
  // init flow deliberately does NOT load this list.
  const needsCustomerList = isAuthenticated && (forcePicker || !activeCustomer);
  const { data: customers = [], isLoading: customersLoading } = useAllCustomers(needsCustomerList);

  // Debug log function that only logs for superusers
  const logger = useCallback(() => createScopedLogger(user, 'CustomerRedirect'), [user]);
  
  const logDebug = useCallback((message: string, data?: any) => {
    logger().debug(message, data);
  }, [logger]);

  // Helper function to get the last active customer ID from storage
  const getLastActiveCustomerId = useCallback(() => {
    try {
      return localStorage.getItem('lastActiveCustomerId');
    } catch (error) {
      console.warn('Failed to get last active customer ID from localStorage:', error);
      return null;
    }
  }, []);

  // Helper function to set the last active customer ID in storage
  const setLastActiveCustomerId = useCallback((customerId: string) => {
    try {
      localStorage.setItem('lastActiveCustomerId', customerId);
    } catch (error) {
      console.warn('Failed to set last active customer ID in localStorage:', error);
    }
  }, []);

  // Session blacklist for customers whose ``selectCustomer`` failed.
  // The customer list returned by ``/api/crm/customers/`` and the
  // gate on ``PUT /api/crm/customers/active/{id}/`` use slightly
  // different access checks server-side, so it's possible to see a
  // customer in the picker that we'll get a 404 / 403 back on when we
  // try to actually switch to it. Without this blacklist the operator
  // hits the failed customer → logout → log back in → auto-select
  // picks the same broken customer → loop. Stored under
  // ``customerSelectBlacklist`` as a JSON array of customer ids.
  const getCustomerBlacklist = useCallback((): string[] => {
    try {
      const raw = localStorage.getItem('customerSelectBlacklist');
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }, []);

  const blacklistCustomer = useCallback((customerId: string | number) => {
    try {
      const current = getCustomerBlacklist();
      const id = String(customerId);
      if (!current.includes(id)) {
        localStorage.setItem(
          'customerSelectBlacklist',
          JSON.stringify([...current, id]),
        );
      }
      // If the failed customer was also the "last active" hint, wipe it
      // so the next login doesn't auto-pick the same broken one.
      if (localStorage.getItem('lastActiveCustomerId') === id) {
        localStorage.removeItem('lastActiveCustomerId');
      }
    } catch (error) {
      console.warn('Failed to update customer blacklist:', error);
    }
  }, [getCustomerBlacklist]);

  // Auto-selection logic
  useEffect(() => {
    const handleSelection = async () => {
      // Don't proceed if we're still initializing, loading, or not authenticated
      if (autoSelectionAttemptedRef.current || !isInitialized || isLoading || !isAuthenticated) {
        return;
      }

      // The auto-pick / "no customers" branches below read the full list,
      // so wait for it to finish loading first. (The ``activeCustomer`` and
      // ``forcePicker`` branches don't need the list, but they're cheap to
      // re-evaluate once it's ready, so gate the whole effect for clarity.)
      if (!activeCustomer && !forcePicker && customersLoading) {
        return;
      }

      // Mark as attempted immediately to prevent duplicate runs
      autoSelectionAttemptedRef.current = true;

      try {
        logDebug('Starting customer auto-selection process');

        // Honour the explicit ``?force=1`` opt-out — operator clicked
        // "Switch Customer" from the topbar and wants the picker,
        // regardless of whether an active customer is already set or
        // there's only one customer available.
        if (forcePicker) {
          logDebug('force=1 query param present — showing selection UI without auto-pick');
          setShowSelectionUI(true);
          return;
        }

        // If user already has an active customer, redirect to intended destination
        if (activeCustomer) {
          logDebug(`User already has active customer (${activeCustomer.name}), redirecting to ${from}`);
          navigate(from, { replace: true });
          return;
        }

        // If no customers available, show error
        if (!customers || customers.length === 0) {
          logDebug('No customers available for selection');
          setShowSelectionUI(true);
          return;
        }

        const blacklist = getCustomerBlacklist();

        // If only one customer, auto-select it (unless we previously
        // saw an access failure on it).
        if (customers.length === 1 && !blacklist.includes(String(customers[0].id))) {
          logDebug(`Only one customer available (${customers[0].name}), auto-selecting`);
          const result = await selectCustomer(customers[0]);

          if (result.success) {
            logDebug('Single customer auto-selection successful');
            setLastActiveCustomerId(String(customers[0].id));
            window.location.replace(from);
            return;
          } else {
            logDebug(`Single customer auto-selection failed: ${result.error}`);
            blacklistCustomer(customers[0].id);
            setCustomerSelectError(result.error || 'Failed to select customer');
            setShowSelectionUI(true);
            return;
          }
        }

        // Try to use last active customer from storage — but skip when
        // it's been blacklisted in a previous failed attempt.
        const lastActiveCustomerId = getLastActiveCustomerId();

        if (lastActiveCustomerId && customers.length > 1) {
          if (blacklist.includes(String(lastActiveCustomerId))) {
            logDebug(`Last active customer ${lastActiveCustomerId} is blacklisted from a prior failure — skipping`);
          } else {
            // Find the last active customer by ID
            const lastActiveCustomer = customers.find(c =>
              String(c.id) === String(lastActiveCustomerId)
            );

            if (lastActiveCustomer) {
              logDebug(`Found last active customer (${lastActiveCustomer.name}), selecting it`);
              const result = await selectCustomer(lastActiveCustomer);

              if (result.success) {
                logDebug('Last active customer selection successful');
                window.location.replace(from);
                return;
              } else {
                logDebug(`Last active customer selection failed: ${result.error}`);
                // Don't punt to the full-screen error state — fall
                // through to the manual picker instead so the operator
                // can pick a different customer in one step.
                blacklistCustomer(lastActiveCustomer.id);
              }
            } else {
              logDebug(`Last active customer ID ${lastActiveCustomerId} not found in available customers`);
              logDebug('Available customer IDs:', customers.map(c => ({ id: c.id, name: c.name })));
            }
          }
        }

        // If we get here, we need to show the selection UI
        logDebug('Auto-selection not possible, showing customer selection UI');
        setShowSelectionUI(true);
      } catch (error) {
        console.error('Error in auto-selection:', error);
        setCustomerSelectError('Error during automatic customer selection');
        setShowSelectionUI(true);
      }
    };
    
    // Start the auto-selection process
    handleSelection();
  }, [
    isInitialized,
    isLoading,
    isAuthenticated,
    activeCustomer,
    user,
    customers,
    customersLoading,
    selectCustomer,
    navigate,
    from,
    logDebug,
    getLastActiveCustomerId,
    setLastActiveCustomerId,
    getCustomerBlacklist,
    blacklistCustomer,
    forcePicker,
  ]);

  // Session unverified (backend unreachable, tokens intact): retry, not /login (#433).
  if (isInitialized && !isLoading && !isAuthenticated && isUnreachable) {
    return <SessionUnreachable />;
  }

  // Redirect to login if not authenticated (after initialization)
  if (isInitialized && !isLoading && !isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: returnTo(location) }} />;
  }

  // Show skeleton loading while initializing or processing
  if (!showSelectionUI) {
    return <MinimalAppSkeleton />;
  }

  // Handle customer selection
  const handleCustomerSelect = async (customer) => {
    setIsLoadingCustomer(true);
    setLoadingCustomerId(customer.id);
    setCustomerSelectError(null);
    
    try {
      logDebug(`User manually selected customer: ${customer.name}`);
      const result = await selectCustomer(customer);
      
      if (result.success) {
        logDebug('Manual customer selection successful');
        setLastActiveCustomerId(String(customer.id));
        // Hard reload — the React tree under AuthProvider keeps too
        // many effects keyed to the previous customer for ``navigate``
        // alone to refresh (cached customers list, sites
        // ``initializedRef``, open React Query subscriptions).
        // ``window.location.replace`` re-runs ``AuthProvider.initialize()``
        // from localStorage + the backend session, both of which
        // ``authService.selectCustomer`` already updated.
        window.location.replace(from);
      } else {
        logDebug(`Manual customer selection failed: ${result.error}`);
        // Blacklist for this browser so the auto-select on the next
        // login doesn't loop back to this same broken customer.
        blacklistCustomer(customer.id);
        // Keep the picker visible with an inline error instead of
        // punting to the full-screen ``CustomerErrorState`` — the
        // operator should be able to pick a different customer in one
        // click without having to log out first.
        setCustomerSelectError(result.error || 'Failed to select customer');
      }
    } catch (error) {
      console.error('Error during customer selection:', error);
      setCustomerSelectError('Error during customer selection');
    } finally {
      setIsLoadingCustomer(false);
      setLoadingCustomerId(null);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  // Full-screen ``CustomerErrorState`` only when there's no useful
  // picker to fall back on (no customers loaded). For other errors we
  // surface them inline above the grid so the operator can pick a
  // different customer in one click instead of having to log out.
  if (customerSelectError && (!customers || customers.length === 0)) {
    return (
      <CustomerErrorState
        error={customerSelectError}
        onRetry={() => {
          setCustomerSelectError(null);
          setShowSelectionUI(false);
          autoSelectionAttemptedRef.current = false;
        }}
        onLogout={handleLogout}
      />
    );
  }

  // Show no customers state if no customers are available
  if (!customers || customers.length === 0) {
    return <NoCustomersState onLogout={handleLogout} />;
  }

  // Show customer selection UI, with the inline error banner when a
  // previous select attempt failed.
  return (
    <Box mih="100vh">
      <CustomerSelectionHeader onLogout={handleLogout} />
      {customerSelectError && (
        <Box maw={672} mx="auto" px="md" pb="md">
          <Alert
            color="red"
            withCloseButton
            onClose={() => setCustomerSelectError(null)}
          >
            {customerSelectError}
          </Alert>
        </Box>
      )}
      <CustomerSelectionGrid
        customers={customers}
        onSelectCustomer={handleCustomerSelect}
        isLoading={isLoadingCustomer}
        loadingCustomerId={loadingCustomerId}
      />
    </Box>
  );
};